/**
 * BotStrategy — Shared bot dispatch logic for AutoplaySystem and StrategicSimulator.
 *
 * Both bots MUST produce identical decisions given the same board state.
 * This module provides:
 * 1. The canonical conveyor-path firing sequence (matches physical belt path)
 * 2. Lightweight board simulation (pure data, no Phaser)
 * 3. Variable-depth lookahead dispatch scoring
 */

import type { CellStack } from '../types/board';
import { cellKey } from '../types/board';
import { getGameplayColorName } from '../config/colors';
import { firingLineKey, type FiringLineDirection } from './firingLineKey';

// ── Types ────────────────────────────────────────────────────────────

export type Direction = FiringLineDirection;

export interface FiringLine {
  direction: Direction;
  lineIndex: number;
}

// ── Firing Line Index (performance optimization) ─────────────────────
// Pre-computed first-target cache for each FiringLine, enabling O(1)
// color-match checks instead of linear cell scans during simulateLap.

export interface FiringLineTarget {
  color: number;   // colorIndex of first reachable target (wall trigger color if wallId)
  row: number;
  col: number;
  /** When present, this target is a blockade wall (not a cube). row/col are -1. */
  wallId?: number;
}

// One entry per FiringLine (same indexing as firingSequence[]).
// null = no reachable target on this line.
export type FiringLineIndex = (FiringLineTarget | null)[];

export interface BotOrb {
  colorIndex: number;
  ammo: number;
}

export interface BotAttacker {
  orbs: BotOrb[];
  size: number;
}

export interface BotMegaBlock {
  id: number;
  colorIndex: number;
  hp: number;
  row: number;
  col: number;
  width: number;
  height: number;
}

export type WallSide = 'top' | 'bottom' | 'left' | 'right';

/**
 * Blockade wall in bot simulation. A wall lives on one edge of the board and
 * blocks all firing lines crossing that edge within [startCell, startCell+length).
 * `cellIndex` is a row for left/right walls, a column for top/bottom walls.
 *
 * Semantics (mirrors ShootingSystem.firePerpendicularShot):
 *   - Matching-color orb firing into a guarded line: hits the wall (HP-1, ammo-1).
 *     The shot stops at the wall and does not penetrate.
 *   - Non-matching-color orb firing into a guarded line: fully blocked —
 *     NO wall damage, NO ammo consumed.
 *   - When wall HP reaches 0, the wall is removed and the previously-guarded
 *     lines are re-opened; the first-visible cell on each re-opened line
 *     becomes the new target.
 */
export interface BotWall {
  id: number;
  side: WallSide;
  startCell: number;
  length: number;
  colorIndex: number;
  hp: number;
}

/**
 * Locked door in bot simulation. Doors are ALWAYS transparent to firing lines
 * (shots skip past door cells regardless of HP), mirroring the PerfectSolver's
 * model (`meta.doorCells` is immutable) and the runtime's `findPerpendicularTarget`
 * which `continue`s past door cells.
 *
 * Semantics (mirrors AtomicSimulator.decrementDoorsForColor + BoardSystem.hitDoor):
 *   - When a cube of color C is cleared anywhere on the board, every door with
 *     `triggerColor === C` has its HP decremented by 1.
 *   - When door HP reaches 0, the door is "unlocked" and removed from tracking.
 *     The door cell itself stays transparent either way — unlocking affects
 *     nothing about shooting. HP tracking only matters for parity with the
 *     solver's state model (so witness replay agrees on `doorHp` values).
 *
 * The door cells are held in `BoardSnapshot.doorCells` (an immutable Set), and
 * the per-door HP/color metadata lives in `BoardSnapshot.doors` so we can decrement
 * HP when matching-color cubes clear. The two representations are complementary:
 *   - `doorCells`: fast pass-through check during findTarget / scanning.
 *   - `doors`: per-door HP bookkeeping for hit accounting.
 */
export interface BotDoor {
  id: number;
  triggerColor: number;
  hp: number;
}

export interface LapResult {
  cubesCleared: number;
  attackerDepleted: boolean;
}

// Candidate from either stack or queue
export interface DispatchCandidate {
  source: 'stack' | 'queue';
  index: number;          // stack slot index or queue lane index
  attacker: BotAttacker;
}

/**
 * For stack candidates, keep only the lowest-ammo attacker per color.
 * When multiple same-color attackers sit in the stack, dispatching the
 * lowest-ammo one first exhausts it faster and frees the slot sooner.
 * Queue candidates are passed through unchanged.
 */
export function filterLowestAmmoPerColor(candidates: DispatchCandidate[]): DispatchCandidate[] {
  // Group stack candidates by their primary color signature
  const stackByColor = new Map<string, DispatchCandidate>();
  const result: DispatchCandidate[] = [];

  for (const cand of candidates) {
    if (cand.source !== 'stack') {
      result.push(cand);
      continue;
    }
    // Build color signature from all orbs (handles multi-orb attackers)
    const colorSig = cand.attacker.orbs
      .map(o => o.colorIndex)
      .sort((a, b) => a - b)
      .join(',');
    const totalAmmo = cand.attacker.orbs.reduce((s, o) => s + o.ammo, 0);

    const existing = stackByColor.get(colorSig);
    if (!existing) {
      stackByColor.set(colorSig, cand);
    } else {
      const existingAmmo = existing.attacker.orbs.reduce((s, o) => s + o.ammo, 0);
      if (totalAmmo < existingAmmo) {
        stackByColor.set(colorSig, cand);
      }
    }
  }

  // Add the winning stack candidate per color
  for (const cand of stackByColor.values()) {
    result.push(cand);
  }

  return result;
}

export interface SearchResult {
  candidateIndex: number;  // index into the viable candidates array
  score: number;
  cubesCleared: number;    // total across all depths
  depthCubes: number[];    // cubes cleared at each depth level
  stackDelta: number;      // cumulative
}

export type DispatchDecision =
  | { action: 'dispatch'; candidate: DispatchCandidate; score: number; breakdown: ScoreBreakdown; candidateCount: number }
  | { action: 'wait' };

// ── Stall Detection State ───────────────────────────────────────────
// Tracks consecutive dispatches that clear 0 cubes. When the bot gets stuck
// cycling stack attackers that can't actually clear anything (cubes are
// blocked behind other colors), the stall detector forces queue candidates
// into consideration to break the deadlock.

export interface StallState {
  lastKnownCubes: number;
  /** Maps colorIndex → cube count at the time this color last completed a stack lap */
  colorLastCubeCount: Map<number, number>;
}

export function createStallState(initialCubes: number): StallState {
  return { lastKnownCubes: initialCubes, colorLastCubeCount: new Map() };
}

/**
 * Update stall state after observing current cube count.
 * Call this BEFORE each dispatch decision with the current board cube count.
 * Mutates the state in-place.
 *
 * When cubes decrease (progress), all stale color entries are cleared —
 * board changes may have exposed new cells for previously-stale colors.
 */
export function updateStallState(state: StallState, currentCubes: number): void {
  if (currentCubes < state.lastKnownCubes) {
    // Progress! Clear all stale color tracking — board changed.
    state.colorLastCubeCount.clear();
    state.lastKnownCubes = currentCubes;
  } else {
    state.lastKnownCubes = currentCubes;
  }
}

/**
 * Record that a color was dispatched from the stack at the given cube count.
 * Call this AFTER a stack dispatch decision is made.
 */
export function recordStallDispatch(state: StallState, colorIndex: number, currentCubes: number): void {
  state.colorLastCubeCount.set(colorIndex, currentCubes);
}

/**
 * Check if a color is stale: it was dispatched from stack before and the board
 * hasn't changed since (same cube count). A stale color won't clear anything new.
 */
export function isColorStale(state: StallState, colorIndex: number, currentCubes: number): boolean {
  const lastCubes = state.colorLastCubeCount.get(colorIndex);
  if (lastCubes === undefined) return false;
  return currentCubes === lastCubes;
}


// ── Bot Trace Types ──────────────────────────────────────────────────

export interface TraceEntry {
  tick: number;
  boardState: { color: string; total: number; reachable: number }[];
  conveyorAttackers: { color: string; ammo: number }[];
  candidates: {
    source: 'stack' | 'queue';
    color: string;
    ammo: number;
    orbs: number;
    depthCubes: number[];
    stackDelta: number;
    totalScore: number;
    hopeless?: boolean;
  }[];
  waitScore: number | null;
  decision: 'dispatch' | 'wait';
  chosenCandidate?: { source: string; color: string; ammo: number; score: number };
  reason: string;
}

export interface BotTrace {
  entries: TraceEntry[];
  push(entry: TraceEntry): void;
}

export function createBotTrace(): BotTrace {
  const entries: TraceEntry[] = [];
  return { entries, push: (e: TraceEntry) => entries.push(e) };
}

/** Get human-readable color name from color index. */
export function colorName(colorIndex: number): string {
  return getGameplayColorName(colorIndex);
}

// ── Board Snapshot (lightweight clone) ───────────────────────────────

export interface BoardSnapshot {
  grid: CellStack[][];
  rows: number;
  cols: number;
  snakeCells: Set<number>;
  megaBlocks: Map<number, BotMegaBlock>;
  megaCells: Set<number>;
  doorCells: Set<number>;
  barricadeCells: Set<number>;
  /** Active blockade walls indexed by id. Empty map = no walls. */
  walls?: Map<number, BotWall>;
  /** Active locked doors indexed by id (HP > 0). Empty map / undefined = no doors. */
  doors?: Map<number, BotDoor>;
}

export function cloneBoard(board: BoardSnapshot): BoardSnapshot {
  const grid = board.grid.map(row => row.map(stack => [...stack]));
  const megaBlocks = new Map<number, BotMegaBlock>();
  for (const [id, mb] of board.megaBlocks) {
    megaBlocks.set(id, { ...mb });
  }
  let walls: Map<number, BotWall> | undefined;
  if (board.walls && board.walls.size > 0) {
    walls = new Map<number, BotWall>();
    for (const [id, w] of board.walls) {
      walls.set(id, { ...w });
    }
  }
  let doors: Map<number, BotDoor> | undefined;
  if (board.doors && board.doors.size > 0) {
    doors = new Map<number, BotDoor>();
    for (const [id, d] of board.doors) {
      doors.set(id, { ...d });
    }
  }
  return {
    grid,
    rows: board.rows,
    cols: board.cols,
    snakeCells: new Set(board.snakeCells),
    megaBlocks,
    megaCells: new Set(board.megaCells),
    doorCells: new Set(board.doorCells),
    barricadeCells: new Set(board.barricadeCells),
    walls,
    doors,
  };
}

export interface GameSnapshot {
  board: BoardSnapshot;
  stackAttackers: BotAttacker[];
  queueLanes: (BotAttacker | null)[][];  // per-lane arrays, position 0 = front
  conveyorAttackers: BotAttacker[];
  maxConveyorSlots: number;
  conveyorSlotsUsed: number;
  maxStackSlots: number;
  stackSlotsUsed: number;
}

export function cloneGameSnapshot(snap: GameSnapshot): GameSnapshot {
  return {
    board: cloneBoard(snap.board),
    stackAttackers: snap.stackAttackers.map(a => cloneAttacker(a)),
    queueLanes: snap.queueLanes.map(lane =>
      lane.map(a => a ? cloneAttacker(a) : null)
    ),
    conveyorAttackers: snap.conveyorAttackers.map(a => cloneAttacker(a)),
    maxConveyorSlots: snap.maxConveyorSlots,
    conveyorSlotsUsed: snap.conveyorSlotsUsed,
    maxStackSlots: snap.maxStackSlots,
    stackSlotsUsed: snap.stackSlotsUsed,
  };
}

// ── Conveyor-Path Firing Sequence ────────────────────────────────────
//
// Matches the physical belt path from ConveyorSystem.createRoundedRectPath:
//   Bottom edge: left -> right   (attacker below board fires UP into rows)
//   Right edge:  bottom -> top   (attacker right of board fires LEFT into rows)
//   Top edge:    right -> left   (attacker above board fires DOWN into columns)
//   Left edge:   top -> bottom   (attacker left of board fires RIGHT into rows)
//
// Each position along the conveyor belt fires into ONE perpendicular line.
// The belt path determines which edge the attacker is near and which row/col
// it's adjacent to.
//
// Bottom edge (left to right): fires UP into each column, col 0..cols-1
// Right edge (bottom to top): fires LEFT into each row, row (rows-1)..0
// Top edge (right to left): fires DOWN into each column, col (cols-1)..0
// Left edge (top to bottom): fires RIGHT into each row, row 0..(rows-1)

export function buildFiringSequence(rows: number, cols: number): FiringLine[] {
  const seq: FiringLine[] = [];

  // Bottom edge: left -> right -> fires UP into columns
  for (let c = 0; c < cols; c++) {
    seq.push({ direction: 'up', lineIndex: c });
  }

  // Right edge: bottom -> top -> fires LEFT into rows
  for (let r = rows - 1; r >= 0; r--) {
    seq.push({ direction: 'left', lineIndex: r });
  }

  // Top edge: right -> left -> fires DOWN into columns
  for (let c = cols - 1; c >= 0; c--) {
    seq.push({ direction: 'down', lineIndex: c });
  }

  // Left edge: top -> bottom -> fires RIGHT into rows
  for (let r = 0; r < rows; r++) {
    seq.push({ direction: 'right', lineIndex: r });
  }

  return seq;
}

// ── Undo/Redo for Board Mutations ────────────────────────────────────

interface UndoAction {
  row: number;
  col: number;
  value: number;  // popped color value, or old mega/wall/door hp
  type: 'pop' | 'snake_delete' | 'mega_hp' | 'mega_destroy' | 'wall_hp' | 'wall_destroy' | 'door_hp' | 'door_destroy';
  // For mega_destroy only:
  megaId?: number;
  megaBlock?: BotMegaBlock;
  megaCellKeys?: number[];
  gridStacks?: { r: number; c: number; stack: number[] }[];
  // For wall_hp / wall_destroy:
  wallId?: number;
  wall?: BotWall;
  // For door_hp / door_destroy:
  doorId?: number;
  door?: BotDoor;
  // FiringLineIndex updates to undo alongside board mutations:
  indexUpdates?: { lineIdx: number; oldTarget: FiringLineTarget | null }[];
}

/**
 * Decrement HP on every active door whose trigger color matches `color`.
 * Records undo actions so the caller can restore the original door state.
 *
 * Door cells are ALWAYS transparent regardless of HP (see `BotDoor` docs) —
 * neither door creation nor door destruction changes firing-line visibility.
 * So the FiringLineIndex never needs updating when doors change; the cached
 * targets (which already skip door cells) stay valid.
 *
 * Mirrors AtomicSimulator.decrementDoorsForColor + ShootingSystem → BoardSystem.hitDoor.
 */
function decrementDoorsForColorWithUndo(
  board: BoardSnapshot,
  color: number,
  undo: UndoAction[],
): void {
  if (!board.doors || board.doors.size === 0) return;
  // Collect matching door ids first so we can mutate the map safely while iterating.
  const matching: number[] = [];
  for (const [id, d] of board.doors) {
    if (d.hp > 0 && d.triggerColor === color) matching.push(id);
  }
  for (const id of matching) {
    const d = board.doors.get(id);
    if (!d || d.hp <= 0) continue;
    const oldHp = d.hp;
    d.hp--;
    if (d.hp <= 0) {
      const savedDoor: BotDoor = { ...d, hp: oldHp };
      board.doors.delete(id);
      undo.push({
        row: -1, col: -1, value: oldHp, type: 'door_destroy',
        doorId: id, door: savedDoor,
      });
    } else {
      undo.push({
        row: -1, col: -1, value: oldHp, type: 'door_hp',
        doorId: id,
      });
    }
  }
}

/**
 * No-undo variant: decrement HP on every active door matching the given color.
 * Used by the non-undo simulateLap path.
 */
function decrementDoorsForColor(board: BoardSnapshot, color: number): void {
  if (!board.doors || board.doors.size === 0) return;
  const matching: number[] = [];
  for (const [id, d] of board.doors) {
    if (d.hp > 0 && d.triggerColor === color) matching.push(id);
  }
  for (const id of matching) {
    const d = board.doors.get(id);
    if (!d || d.hp <= 0) continue;
    d.hp--;
    if (d.hp <= 0) board.doors.delete(id);
  }
}

/**
 * Hit a blockade wall — decrement its HP (and remove when HP reaches 0).
 * Records an undo action so the caller can restore the original wall state.
 * When the wall is destroyed, FiringLineIndex entries for the previously
 * guarded lines are updated to their new first-visible cells.
 */
function hitWallWithUndo(
  board: BoardSnapshot,
  wallId: number,
  undo: UndoAction[],
  fli?: FiringLineIndex,
  firingSequence?: FiringLine[],
): void {
  if (!board.walls) return;
  const wall = board.walls.get(wallId);
  if (!wall || wall.hp <= 0) return;

  const oldHp = wall.hp;
  wall.hp--;

  if (wall.hp <= 0) {
    // Wall destroyed — save snapshot, remove from board, and refresh FLI
    // entries for the now-open lines.
    const savedWall: BotWall = { ...wall, hp: oldHp };
    board.walls.delete(wallId);
    const indexUpdates: { lineIdx: number; oldTarget: FiringLineTarget | null }[] = [];
    if (fli && firingSequence) {
      const lineIndices = wallFiringLineIndices(savedWall, board.rows, board.cols);
      for (const idx of lineIndices) {
        const oldTarget = fli[idx];
        const line = firingSequence[idx];
        const newTarget = findFirstTarget(board, line.direction, line.lineIndex);
        if (oldTarget !== newTarget) {
          indexUpdates.push({ lineIdx: idx, oldTarget });
          fli[idx] = newTarget;
        }
      }
    }
    undo.push({
      row: -1, col: -1, value: oldHp, type: 'wall_destroy',
      wallId, wall: savedWall,
      indexUpdates: indexUpdates.length > 0 ? indexUpdates : undefined,
    });
  } else {
    // Wall HP decreased but still blocking — cached FLI entry (wall-target)
    // still points at the same wall with same trigger color, so no index
    // change is needed. The wall's hp field inside BotWall is a mutable
    // number; the FLI entry doesn't carry hp, so the entry is still valid.
    undo.push({
      row: -1, col: -1, value: oldHp, type: 'wall_hp',
      wallId,
    });
  }
}

function hitCellWithUndo(
  board: BoardSnapshot,
  row: number,
  col: number,
  undo: UndoAction[],
  fli?: FiringLineIndex,
  firingSequence?: FiringLine[],
): void {
  const key = cellKey(row, col);

  if (board.megaCells.has(key)) {
    for (const [id, mb] of board.megaBlocks) {
      if (row >= mb.row && row < mb.row + mb.height &&
          col >= mb.col && col < mb.col + mb.width) {
        const oldHp = mb.hp;
        const hitColor = mb.colorIndex;
        mb.hp--;
        if (mb.hp <= 0) {
          // Save all cell grid stacks BEFORE clearing
          const savedGridStacks: { r: number; c: number; stack: number[] }[] = [];
          const savedCellKeys: number[] = [];
          for (let r = mb.row; r < mb.row + mb.height; r++) {
            for (let c = mb.col; c < mb.col + mb.width; c++) {
              const ck = cellKey(r, c);
              savedCellKeys.push(ck);
              savedGridStacks.push({ r, c, stack: [...board.grid[r][c]] });
              board.megaCells.delete(ck);
              board.grid[r][c] = [];
            }
          }
          // Save mega block before deleting
          const savedMegaBlock: BotMegaBlock = { ...mb, hp: oldHp };
          board.megaBlocks.delete(id);
          const indexUpdates: { lineIdx: number; oldTarget: FiringLineTarget | null }[] = [];
          if (fli && firingSequence) {
            updateFiringLineIndexForMegaDestroy(board, fli, firingSequence, savedMegaBlock, board.rows, board.cols, indexUpdates);
          }
          undo.push({
            row, col, value: oldHp, type: 'mega_destroy',
            megaId: id, megaBlock: savedMegaBlock,
            megaCellKeys: savedCellKeys, gridStacks: savedGridStacks,
            indexUpdates: indexUpdates.length > 0 ? indexUpdates : undefined,
          });
        } else {
          // HP decreased but block not destroyed — the first target on affected lines
          // hasn't changed (same cell, same color, still present).
          undo.push({ row, col, value: oldHp, type: 'mega_hp', megaId: id });
        }
        // Mega block hits count toward door decrement (mirrors ShootingSystem —
        // every successful orb fire triggers `hitDoor(orb.colorIndex)`, and the
        // orb color matches the mega's colorIndex).
        decrementDoorsForColorWithUndo(board, hitColor, undo);
        return;
      }
    }
    return;
  }

  if (board.snakeCells.has(key)) {
    const bottomRow = findSnakeBottomRow(board, row, col);
    const stack = board.grid[bottomRow][col];
    if (stack.length > 0) {
      const popped = stack.pop()!;
      const indexUpdates: { lineIdx: number; oldTarget: FiringLineTarget | null }[] = [];
      if (stack.length === 0) {
        board.snakeCells.delete(cellKey(bottomRow, col));
        // Snake bottom cleared — update firing lines through (bottomRow, col)
        if (fli && firingSequence) {
          updateFiringLineIndex(board, fli, firingSequence, bottomRow, col, board.rows, board.cols, indexUpdates);
        }
        undo.push({
          row: bottomRow, col, value: popped, type: 'snake_delete',
          indexUpdates: indexUpdates.length > 0 ? indexUpdates : undefined,
        });
      } else {
        // Snake shortened — color at top may have changed
        if (fli && firingSequence) {
          updateFiringLineIndex(board, fli, firingSequence, bottomRow, col, board.rows, board.cols, indexUpdates);
        }
        undo.push({
          row: bottomRow, col, value: popped, type: 'pop',
          indexUpdates: indexUpdates.length > 0 ? indexUpdates : undefined,
        });
      }
      // Door decrement for cleared snake cube
      decrementDoorsForColorWithUndo(board, popped, undo);
    }
    return;
  }

  const stack = board.grid[row][col];
  if (stack.length > 0) {
    const popped = stack.pop()!;
    const indexUpdates: { lineIdx: number; oldTarget: FiringLineTarget | null }[] = [];
    if (fli && firingSequence) {
      updateFiringLineIndex(board, fli, firingSequence, row, col, board.rows, board.cols, indexUpdates);
    }
    undo.push({
      row, col, value: popped, type: 'pop',
      indexUpdates: indexUpdates.length > 0 ? indexUpdates : undefined,
    });
    // Door decrement for cleared cube
    decrementDoorsForColorWithUndo(board, popped, undo);
  }
}

function simulateLapWithUndo(
  board: BoardSnapshot,
  attacker: BotAttacker,
  firingSequence: FiringLine[],
  fli?: FiringLineIndex,
): { result: LapResult; undo: UndoAction[] } {
  const undo: UndoAction[] = [];
  let cubesCleared = 0;
  const firedLines = new Set<number>();

  for (let i = 0; i < firingSequence.length; i++) {
    if (attacker.orbs.every(o => o.ammo <= 0)) break;

    const line = firingSequence[i];
    const lineKey = firingLineKey(line.direction, line.lineIndex);
    if (firedLines.has(lineKey)) continue;

    if (fli) {
      // Fast path: use pre-computed first-target cache
      const cached = fli[i];
      if (!cached) continue; // no target on this line
      let matched = false;
      for (const orb of attacker.orbs) {
        if (orb.ammo <= 0) continue;
        if (orb.colorIndex === cached.color) {
          if (cached.wallId !== undefined) {
            // Wall hit: matching-color orb damages the wall. No cube cleared.
            // hitWallWithUndo refreshes FLI entries if the wall is destroyed.
            hitWallWithUndo(board, cached.wallId, undo, fli, firingSequence);
          } else {
            hitCellWithUndo(board, cached.row, cached.col, undo, fli, firingSequence);
            cubesCleared++;
          }
          orb.ammo--;
          firedLines.add(lineKey);
          matched = true;
          break;
        }
      }
      if (!matched) {
        // First target (cube or wall) exists but no orb matches. Line is
        // effectively blocked — no ammo consumed, nothing cleared.
      }
    } else {
      // Original path (no index): scan cells/walls directly
      for (const orb of attacker.orbs) {
        if (orb.ammo <= 0) continue;
        const target = findTarget(board, orb.colorIndex, line.direction, line.lineIndex);
        if (target) {
          if (target.wallId !== undefined) {
            hitWallWithUndo(board, target.wallId, undo);
          } else {
            hitCellWithUndo(board, target.row, target.col, undo);
            cubesCleared++;
          }
          orb.ammo--;
          firedLines.add(lineKey);
          break;
        }
      }
    }
  }

  const attackerDepleted = attacker.orbs.every(o => o.ammo <= 0);
  return { result: { cubesCleared, attackerDepleted }, undo };
}

function undoLap(board: BoardSnapshot, actions: UndoAction[], fli?: FiringLineIndex): void {
  for (let i = actions.length - 1; i >= 0; i--) {
    const a = actions[i];
    // Restore FiringLineIndex entries BEFORE restoring board state,
    // since index entries record the old target pre-mutation.
    if (fli && a.indexUpdates) {
      for (let j = a.indexUpdates.length - 1; j >= 0; j--) {
        const upd = a.indexUpdates[j];
        fli[upd.lineIdx] = upd.oldTarget;
      }
    }
    switch (a.type) {
      case 'pop':
        board.grid[a.row][a.col].push(a.value);
        break;
      case 'snake_delete':
        board.grid[a.row][a.col].push(a.value);
        board.snakeCells.add(cellKey(a.row, a.col));
        break;
      case 'mega_hp': {
        const mb = board.megaBlocks.get(a.megaId!);
        if (mb) mb.hp = a.value;
        break;
      }
      case 'mega_destroy':
        board.megaBlocks.set(a.megaId!, a.megaBlock!);
        for (const ck of a.megaCellKeys!) board.megaCells.add(ck);
        for (const gs of a.gridStacks!) board.grid[gs.r][gs.c] = gs.stack;
        break;
      case 'wall_hp': {
        if (board.walls) {
          const w = board.walls.get(a.wallId!);
          if (w) w.hp = a.value;
        }
        break;
      }
      case 'wall_destroy': {
        if (!board.walls) board.walls = new Map<number, BotWall>();
        board.walls.set(a.wallId!, { ...a.wall! });
        break;
      }
      case 'door_hp': {
        if (board.doors) {
          const d = board.doors.get(a.doorId!);
          if (d) d.hp = a.value;
        }
        break;
      }
      case 'door_destroy': {
        if (!board.doors) board.doors = new Map<number, BotDoor>();
        board.doors.set(a.doorId!, { ...a.door! });
        break;
      }
    }
  }
}

// ── Wall Helpers ──────────────────────────────────────────────────────

/**
 * Map a firing direction to the board side on which a wall would block it.
 * Mirrors ShootingSystem.wallSideForDirection.
 *   - firing 'right' → attacker on left → wall on 'left' edge blocks.
 *   - firing 'left'  → attacker on right → wall on 'right' edge blocks.
 *   - firing 'down'  → attacker on top → wall on 'top' edge blocks.
 *   - firing 'up'    → attacker on bottom → wall on 'bottom' edge blocks.
 */
function wallSideForDirection(direction: Direction): WallSide {
  switch (direction) {
    case 'right': return 'left';
    case 'left':  return 'right';
    case 'down':  return 'top';
    case 'up':    return 'bottom';
  }
}

/**
 * Find the active wall (HP > 0) blocking a firing line from its edge.
 * For left/right directions, lineIndex is the row; for up/down, the col.
 * Returns null if no wall guards this line.
 */
function findBlockingWall(
  board: BoardSnapshot,
  direction: Direction,
  lineIndex: number,
): BotWall | null {
  if (!board.walls || board.walls.size === 0) return null;
  const side = wallSideForDirection(direction);
  for (const wall of board.walls.values()) {
    if (wall.hp <= 0) continue;
    if (wall.side !== side) continue;
    if (lineIndex >= wall.startCell && lineIndex < wall.startCell + wall.length) {
      return wall;
    }
  }
  return null;
}

/**
 * Collect firingSequence indices for all lines a wall guards. Used for
 * FiringLineIndex updates when a wall is created/destroyed.
 */
function wallFiringLineIndices(
  wall: BotWall,
  rows: number,
  cols: number,
): number[] {
  const out: number[] = [];
  // For each cell the wall covers, the corresponding firing line crosses
  // that edge from the side OPPOSITE the wall.
  // side 'left'   → fires 'right' into row=cellIndex
  // side 'right'  → fires 'left'  into row=cellIndex
  // side 'top'    → fires 'down'  into col=cellIndex
  // side 'bottom' → fires 'up'    into col=cellIndex
  for (let k = 0; k < wall.length; k++) {
    const cellIndex = wall.startCell + k;
    let dir: Direction;
    switch (wall.side) {
      case 'left':   dir = 'right'; break;
      case 'right':  dir = 'left';  break;
      case 'top':    dir = 'down';  break;
      case 'bottom': dir = 'up';    break;
    }
    out.push(firingSeqIndex(dir, cellIndex, rows, cols));
  }
  return out;
}

// ── Board Query Helpers ──────────────────────────────────────────────

function isSkipped(board: BoardSnapshot, row: number, col: number): boolean {
  const key = cellKey(row, col);
  return board.doorCells.has(key) || board.barricadeCells.has(key);
}

function getTopColor(board: BoardSnapshot, row: number, col: number): number | null {
  const key = cellKey(row, col);
  if (board.megaCells.has(key)) {
    for (const mb of board.megaBlocks.values()) {
      if (row >= mb.row && row < mb.row + mb.height &&
          col >= mb.col && col < mb.col + mb.width) {
        return mb.colorIndex;
      }
    }
    return null;
  }
  const stack = board.grid[row][col];
  return stack.length > 0 ? stack[stack.length - 1] : null;
}

function findSnakeBottomRow(board: BoardSnapshot, startRow: number, col: number): number {
  let bottomRow = startRow;
  for (let r = startRow + 1; r < board.rows; r++) {
    if (board.snakeCells.has(cellKey(r, col)) && board.grid[r][col].length > 0) {
      bottomRow = r;
    } else {
      break;
    }
  }
  return bottomRow;
}

export function findTarget(
  board: BoardSnapshot,
  colorIndex: number,
  direction: Direction,
  lineIndex: number,
): { row: number; col: number; wallId?: number } | null {
  // Blockade wall gating: a live wall on this line's edge blocks the line.
  // Matching color → target is the wall (row=-1, col=-1, wallId set). Caller
  // interprets this as "hit the wall" (see hitCellWithUndo).
  // Non-matching color → fully blocked; no target.
  const wall = findBlockingWall(board, direction, lineIndex);
  if (wall) {
    if (wall.colorIndex === colorIndex) {
      return { row: -1, col: -1, wallId: wall.id };
    }
    return null;
  }

  if (direction === 'right') {
    const r = lineIndex;
    for (let c = 0; c < board.cols; c++) {
      if (isSkipped(board, r, c)) continue;
      const topColor = getTopColor(board, r, c);
      if (topColor === null) continue;
      if (board.snakeCells.has(cellKey(r, c))) return null;
      return topColor === colorIndex ? { row: r, col: c } : null;
    }
  } else if (direction === 'left') {
    const r = lineIndex;
    for (let c = board.cols - 1; c >= 0; c--) {
      if (isSkipped(board, r, c)) continue;
      const topColor = getTopColor(board, r, c);
      if (topColor === null) continue;
      if (board.snakeCells.has(cellKey(r, c))) return null;
      return topColor === colorIndex ? { row: r, col: c } : null;
    }
  } else if (direction === 'down') {
    const c = lineIndex;
    for (let r = 0; r < board.rows; r++) {
      if (isSkipped(board, r, c)) continue;
      const topColor = getTopColor(board, r, c);
      if (topColor === null) continue;
      return topColor === colorIndex ? { row: r, col: c } : null;
    }
  } else if (direction === 'up') {
    const c = lineIndex;
    for (let r = board.rows - 1; r >= 0; r--) {
      if (isSkipped(board, r, c)) continue;
      const topColor = getTopColor(board, r, c);
      if (topColor === null) continue;
      if (board.snakeCells.has(cellKey(r, c))) return null;
      return topColor === colorIndex ? { row: r, col: c } : null;
    }
  }
  return null;
}

/**
 * Hit a blockade wall — no undo tracking. Used by the non-undo simulateLap.
 * Decrements HP and removes the wall when HP reaches 0.
 */
function hitWall(board: BoardSnapshot, wallId: number): void {
  if (!board.walls) return;
  const wall = board.walls.get(wallId);
  if (!wall || wall.hp <= 0) return;
  wall.hp--;
  if (wall.hp <= 0) board.walls.delete(wallId);
}

function hitCell(board: BoardSnapshot, row: number, col: number): void {
  const key = cellKey(row, col);

  if (board.megaCells.has(key)) {
    for (const [id, mb] of board.megaBlocks) {
      if (row >= mb.row && row < mb.row + mb.height &&
          col >= mb.col && col < mb.col + mb.width) {
        const hitColor = mb.colorIndex;
        mb.hp--;
        if (mb.hp <= 0) {
          for (let r = mb.row; r < mb.row + mb.height; r++) {
            for (let c = mb.col; c < mb.col + mb.width; c++) {
              board.megaCells.delete(cellKey(r, c));
              board.grid[r][c] = [];
            }
          }
          board.megaBlocks.delete(id);
        }
        decrementDoorsForColor(board, hitColor);
        return;
      }
    }
    return;
  }

  if (board.snakeCells.has(key)) {
    const bottomRow = findSnakeBottomRow(board, row, col);
    const stack = board.grid[bottomRow][col];
    if (stack.length > 0) {
      const popped = stack.pop()!;
      if (stack.length === 0) {
        board.snakeCells.delete(cellKey(bottomRow, col));
      }
      decrementDoorsForColor(board, popped);
    }
    return;
  }

  const stack = board.grid[row][col];
  if (stack.length > 0) {
    const popped = stack.pop()!;
    decrementDoorsForColor(board, popped);
  }
}

/**
 * Hit a wall and update the FiringLineIndex (no undo recording). Used by the
 * non-undo simulateLap with index. Decrements HP; when the wall breaks,
 * refreshes FLI entries for all previously guarded lines.
 */
function hitWallAndUpdateIndex(
  board: BoardSnapshot,
  wallId: number,
  fli: FiringLineIndex,
  firingSequence: FiringLine[],
): void {
  if (!board.walls) return;
  const wall = board.walls.get(wallId);
  if (!wall || wall.hp <= 0) return;
  wall.hp--;
  if (wall.hp <= 0) {
    const savedWall: BotWall = { ...wall };
    board.walls.delete(wallId);
    const lineIndices = wallFiringLineIndices(savedWall, board.rows, board.cols);
    for (const idx of lineIndices) {
      const line = firingSequence[idx];
      fli[idx] = findFirstTarget(board, line.direction, line.lineIndex);
    }
  }
}

/**
 * Hit a cell and update the FiringLineIndex (no undo recording).
 * Used by simulateLap (the non-undo variant) when a FiringLineIndex is available.
 */
function hitCellAndUpdateIndex(
  board: BoardSnapshot,
  row: number,
  col: number,
  fli: FiringLineIndex,
  firingSequence: FiringLine[],
): void {
  const key = cellKey(row, col);

  if (board.megaCells.has(key)) {
    for (const [id, mb] of board.megaBlocks) {
      if (row >= mb.row && row < mb.row + mb.height &&
          col >= mb.col && col < mb.col + mb.width) {
        const hitColor = mb.colorIndex;
        mb.hp--;
        if (mb.hp <= 0) {
          // Save mega block info before clearing
          const savedMb: BotMegaBlock = { ...mb };
          for (let r = mb.row; r < mb.row + mb.height; r++) {
            for (let c = mb.col; c < mb.col + mb.width; c++) {
              board.megaCells.delete(cellKey(r, c));
              board.grid[r][c] = [];
            }
          }
          board.megaBlocks.delete(id);
          updateFiringLineIndexForMegaDestroyNoUndo(board, fli, firingSequence, savedMb, board.rows, board.cols);
        }
        // If HP > 0, cell still exists at same position — no index update needed
        decrementDoorsForColor(board, hitColor);
        return;
      }
    }
    return;
  }

  if (board.snakeCells.has(key)) {
    const bottomRow = findSnakeBottomRow(board, row, col);
    const stack = board.grid[bottomRow][col];
    if (stack.length > 0) {
      const popped = stack.pop()!;
      if (stack.length === 0) {
        board.snakeCells.delete(cellKey(bottomRow, col));
      }
      // Snake cell changed — update index for affected lines through the bottom row
      updateFiringLineIndexNoUndo(board, fli, firingSequence, bottomRow, col, board.rows, board.cols);
      decrementDoorsForColor(board, popped);
    }
    return;
  }

  const stack = board.grid[row][col];
  if (stack.length > 0) {
    const popped = stack.pop()!;
    updateFiringLineIndexNoUndo(board, fli, firingSequence, row, col, board.rows, board.cols);
    decrementDoorsForColor(board, popped);
  }
}

// ── Reachability Map ─────────────────────────────────────────────────

function firstTargetColor(
  board: BoardSnapshot,
  direction: Direction,
  lineIndex: number,
): number | null {
  // A live wall on this line's edge gates the line; only matching-color shots
  // pass (and even then, they hit the wall, not any cube). Treat the line's
  // "first target color" as the wall's trigger color — that is what an orb
  // would need to carry to spend ammo here.
  const wall = findBlockingWall(board, direction, lineIndex);
  if (wall) return wall.colorIndex;

  if (direction === 'right') {
    const r = lineIndex;
    for (let c = 0; c < board.cols; c++) {
      if (isSkipped(board, r, c)) continue;
      const topColor = getTopColor(board, r, c);
      if (topColor === null) continue;
      if (board.snakeCells.has(cellKey(r, c))) return null;
      return topColor;
    }
  } else if (direction === 'left') {
    const r = lineIndex;
    for (let c = board.cols - 1; c >= 0; c--) {
      if (isSkipped(board, r, c)) continue;
      const topColor = getTopColor(board, r, c);
      if (topColor === null) continue;
      if (board.snakeCells.has(cellKey(r, c))) return null;
      return topColor;
    }
  } else if (direction === 'down') {
    const c = lineIndex;
    for (let r = 0; r < board.rows; r++) {
      if (isSkipped(board, r, c)) continue;
      const topColor = getTopColor(board, r, c);
      if (topColor === null) continue;
      return topColor;
    }
  } else { // up
    const c = lineIndex;
    for (let r = board.rows - 1; r >= 0; r--) {
      if (isSkipped(board, r, c)) continue;
      const topColor = getTopColor(board, r, c);
      if (topColor === null) continue;
      if (board.snakeCells.has(cellKey(r, c))) return null;
      return topColor;
    }
  }
  return null;
}

/**
 * Find the first reachable target on a firing line, returning its color AND position.
 * Uses the exact same logic as findTarget / firstTargetColor — skips doors/barricades,
 * blocks on snakes (for non-down directions), and returns null if line is empty.
 */
function findFirstTarget(
  board: BoardSnapshot,
  direction: Direction,
  lineIndex: number,
): FiringLineTarget | null {
  // Wall gate: return a synthetic wall-target entry. row/col are -1 (not a
  // real cell); the lap simulator detects wallId and dispatches to the wall
  // hit path instead of hitCell.
  const wall = findBlockingWall(board, direction, lineIndex);
  if (wall) return { color: wall.colorIndex, row: -1, col: -1, wallId: wall.id };

  if (direction === 'right') {
    const r = lineIndex;
    for (let c = 0; c < board.cols; c++) {
      if (isSkipped(board, r, c)) continue;
      const topColor = getTopColor(board, r, c);
      if (topColor === null) continue;
      if (board.snakeCells.has(cellKey(r, c))) return null;
      return { color: topColor, row: r, col: c };
    }
  } else if (direction === 'left') {
    const r = lineIndex;
    for (let c = board.cols - 1; c >= 0; c--) {
      if (isSkipped(board, r, c)) continue;
      const topColor = getTopColor(board, r, c);
      if (topColor === null) continue;
      if (board.snakeCells.has(cellKey(r, c))) return null;
      return { color: topColor, row: r, col: c };
    }
  } else if (direction === 'down') {
    const c = lineIndex;
    for (let r = 0; r < board.rows; r++) {
      if (isSkipped(board, r, c)) continue;
      const topColor = getTopColor(board, r, c);
      if (topColor === null) continue;
      return { color: topColor, row: r, col: c };
    }
  } else { // up
    const c = lineIndex;
    for (let r = board.rows - 1; r >= 0; r--) {
      if (isSkipped(board, r, c)) continue;
      const topColor = getTopColor(board, r, c);
      if (topColor === null) continue;
      if (board.snakeCells.has(cellKey(r, c))) return null;
      return { color: topColor, row: r, col: c };
    }
  }
  return null;
}

/**
 * Build a FiringLineIndex: pre-compute the first reachable target for every FiringLine.
 * O(firingLines * avgScanDepth) — done once, then maintained incrementally.
 */
export function buildFiringLineIndex(
  board: BoardSnapshot,
  firingSequence: FiringLine[],
): FiringLineIndex {
  const index: FiringLineIndex = new Array(firingSequence.length);
  for (let i = 0; i < firingSequence.length; i++) {
    const line = firingSequence[i];
    index[i] = findFirstTarget(board, line.direction, line.lineIndex);
  }
  return index;
}

/**
 * Map a (direction, lineIndex) pair to the index in firingSequence[].
 * Firing sequence order:
 *   [0..cols-1]                 = bottom edge, direction='up',    lineIndex=0..cols-1
 *   [cols..cols+rows-1]         = right edge,  direction='left',  lineIndex=rows-1..0
 *   [cols+rows..2*cols+rows-1]  = top edge,    direction='down',  lineIndex=cols-1..0
 *   [2*cols+rows..2*(cols+rows)-1] = left edge, direction='right', lineIndex=0..rows-1
 */
function firingSeqIndex(direction: Direction, lineIndex: number, rows: number, cols: number): number {
  switch (direction) {
    case 'up':    return lineIndex;                              // col index
    case 'left':  return cols + (rows - 1 - lineIndex);          // row index, reversed
    case 'down':  return cols + rows + (cols - 1 - lineIndex);   // col index, reversed
    case 'right': return 2 * cols + rows + lineIndex;            // row index
  }
}

/**
 * After clearing cell (row, col), update FiringLineIndex entries for all
 * FiringLines that pass through that cell. Only re-scans lines where the
 * cleared cell WAS the cached first target.
 */
function updateFiringLineIndex(
  board: BoardSnapshot,
  lineIndex: FiringLineIndex,
  firingSequence: FiringLine[],
  clearedRow: number,
  clearedCol: number,
  rows: number,
  cols: number,
  indexUpdates: { lineIdx: number; oldTarget: FiringLineTarget | null }[],
): void {
  // A cell at (r, c) can be the first target on up to 4 firing lines:
  // 1. 'right' along row r  (from left edge)
  // 2. 'left'  along row r  (from right edge)
  // 3. 'down'  along col c  (from top edge)
  // 4. 'up'    along col c  (from bottom edge)
  const affectedIndices = [
    firingSeqIndex('right', clearedRow, rows, cols),
    firingSeqIndex('left',  clearedRow, rows, cols),
    firingSeqIndex('down',  clearedCol, rows, cols),
    firingSeqIndex('up',    clearedCol, rows, cols),
  ];

  for (const idx of affectedIndices) {
    const cached = lineIndex[idx];
    if (!cached) continue; // no target cached — clearing a cell can't affect an empty line
    // Only re-scan if the cleared cell was the cached first target
    if (cached.row === clearedRow && cached.col === clearedCol) {
      indexUpdates.push({ lineIdx: idx, oldTarget: cached });
      const line = firingSequence[idx];
      lineIndex[idx] = findFirstTarget(board, line.direction, line.lineIndex);
    }
  }
}

/**
 * After clearing a cell due to mega block destruction (entire block cleared),
 * update FiringLineIndex for ALL cells in the mega block's footprint.
 */
function updateFiringLineIndexForMegaDestroy(
  board: BoardSnapshot,
  lineIndex: FiringLineIndex,
  firingSequence: FiringLine[],
  mb: BotMegaBlock,
  rows: number,
  cols: number,
  indexUpdates: { lineIdx: number; oldTarget: FiringLineTarget | null }[],
): void {
  // Track which firingSequence indices we've already re-scanned to avoid
  // redundant work when multiple cells in the block share a firing line.
  const rescanned = new Set<number>();

  for (let r = mb.row; r < mb.row + mb.height; r++) {
    for (let c = mb.col; c < mb.col + mb.width; c++) {
      const affectedIndices = [
        firingSeqIndex('right', r, rows, cols),
        firingSeqIndex('left',  r, rows, cols),
        firingSeqIndex('down',  c, rows, cols),
        firingSeqIndex('up',    c, rows, cols),
      ];
      for (const idx of affectedIndices) {
        if (rescanned.has(idx)) continue;
        const cached = lineIndex[idx];
        if (!cached) continue;
        // Check if cached target falls within the mega block footprint
        if (cached.row >= mb.row && cached.row < mb.row + mb.height &&
            cached.col >= mb.col && cached.col < mb.col + mb.width) {
          rescanned.add(idx);
          indexUpdates.push({ lineIdx: idx, oldTarget: cached });
          const line = firingSequence[idx];
          lineIndex[idx] = findFirstTarget(board, line.direction, line.lineIndex);
        }
      }
    }
  }
}

/**
 * Update FiringLineIndex after clearing a cell — no undo recording.
 * Used by simulateLap (the non-undo variant).
 */
function updateFiringLineIndexNoUndo(
  board: BoardSnapshot,
  lineIndex: FiringLineIndex,
  firingSequence: FiringLine[],
  clearedRow: number,
  clearedCol: number,
  rows: number,
  cols: number,
): void {
  const affectedIndices = [
    firingSeqIndex('right', clearedRow, rows, cols),
    firingSeqIndex('left',  clearedRow, rows, cols),
    firingSeqIndex('down',  clearedCol, rows, cols),
    firingSeqIndex('up',    clearedCol, rows, cols),
  ];
  for (const idx of affectedIndices) {
    const cached = lineIndex[idx];
    if (!cached) continue;
    if (cached.row === clearedRow && cached.col === clearedCol) {
      const line = firingSequence[idx];
      lineIndex[idx] = findFirstTarget(board, line.direction, line.lineIndex);
    }
  }
}

/**
 * Update FiringLineIndex after mega block destruction — no undo recording.
 * Used by simulateLap (the non-undo variant) via hitCell.
 */
function updateFiringLineIndexForMegaDestroyNoUndo(
  board: BoardSnapshot,
  lineIndex: FiringLineIndex,
  firingSequence: FiringLine[],
  mb: BotMegaBlock,
  rows: number,
  cols: number,
): void {
  const rescanned = new Set<number>();
  for (let r = mb.row; r < mb.row + mb.height; r++) {
    for (let c = mb.col; c < mb.col + mb.width; c++) {
      const affectedIndices = [
        firingSeqIndex('right', r, rows, cols),
        firingSeqIndex('left',  r, rows, cols),
        firingSeqIndex('down',  c, rows, cols),
        firingSeqIndex('up',    c, rows, cols),
      ];
      for (const idx of affectedIndices) {
        if (rescanned.has(idx)) continue;
        const cached = lineIndex[idx];
        if (!cached) continue;
        if (cached.row >= mb.row && cached.row < mb.row + mb.height &&
            cached.col >= mb.col && cached.col < mb.col + mb.width) {
          rescanned.add(idx);
          const line = firingSequence[idx];
          lineIndex[idx] = findFirstTarget(board, line.direction, line.lineIndex);
        }
      }
    }
  }
}

export function buildReachMap(board: BoardSnapshot): Map<number, number> {
  const colorCounts = new Map<number, number>();

  for (let r = 0; r < board.rows; r++) {
    const color = firstTargetColor(board, 'right', r);
    if (color !== null) colorCounts.set(color, (colorCounts.get(color) ?? 0) + 1);
  }
  for (let r = 0; r < board.rows; r++) {
    const color = firstTargetColor(board, 'left', r);
    if (color !== null) colorCounts.set(color, (colorCounts.get(color) ?? 0) + 1);
  }
  for (let c = 0; c < board.cols; c++) {
    const color = firstTargetColor(board, 'down', c);
    if (color !== null) colorCounts.set(color, (colorCounts.get(color) ?? 0) + 1);
  }
  for (let c = 0; c < board.cols; c++) {
    const color = firstTargetColor(board, 'up', c);
    if (color !== null) colorCounts.set(color, (colorCounts.get(color) ?? 0) + 1);
  }

  return colorCounts;
}

// ── Lap Simulation ───────────────────────────────────────────────────

/**
 * Simulate one full conveyor lap for a single attacker on the given board.
 * Fires one shot per firing line in conveyor-path order, updating the board
 * after each shot. Returns how many cubes were cleared and whether the
 * attacker's ammo is fully depleted.
 */
export function simulateLap(
  board: BoardSnapshot,
  attacker: BotAttacker,
  firingSequence: FiringLine[],
  fli?: FiringLineIndex,
): LapResult {
  let cubesCleared = 0;
  const firedLines = new Set<number>();

  for (let i = 0; i < firingSequence.length; i++) {
    if (attacker.orbs.every(o => o.ammo <= 0)) break;

    const line = firingSequence[i];
    const lineKey = firingLineKey(line.direction, line.lineIndex);
    if (firedLines.has(lineKey)) continue;

    if (fli) {
      // Fast path: use pre-computed first-target cache
      const cached = fli[i];
      if (!cached) continue;
      for (const orb of attacker.orbs) {
        if (orb.ammo <= 0) continue;
        if (orb.colorIndex === cached.color) {
          if (cached.wallId !== undefined) {
            hitWallAndUpdateIndex(board, cached.wallId, fli, firingSequence);
          } else {
            hitCellAndUpdateIndex(board, cached.row, cached.col, fli, firingSequence);
            cubesCleared++;
          }
          orb.ammo--;
          firedLines.add(lineKey);
          break;
        }
      }
    } else {
      for (const orb of attacker.orbs) {
        if (orb.ammo <= 0) continue;
        const target = findTarget(board, orb.colorIndex, line.direction, line.lineIndex);
        if (target) {
          if (target.wallId !== undefined) {
            hitWall(board, target.wallId);
          } else {
            hitCell(board, target.row, target.col);
            cubesCleared++;
          }
          orb.ammo--;
          firedLines.add(lineKey);
          break;
        }
      }
    }
  }

  const attackerDepleted = attacker.orbs.every(o => o.ammo <= 0);
  return { cubesCleared, attackerDepleted };
}

// ── Scoring Helpers ──────────────────────────────────────────────────

export function cloneAttacker(att: BotAttacker): BotAttacker {
  return {
    orbs: att.orbs.map(o => ({ colorIndex: o.colorIndex, ammo: o.ammo })),
    size: att.size,
  };
}

// Stack penalty constants — still used by searchBest (recursive lookahead).
const STACK_PENALTY_BASE = 1.5;
const STACK_PENALTY_SCALED = 2.0;

/**
 * Compute dynamic stack penalty weight based on how full the stack is.
 * Used by searchBest for recursive depth-N evaluation.
 */
function stackPenaltyWeight(stackSlotsUsed: number, maxStackSlots: number): number {
  if (maxStackSlots <= 0) return STACK_PENALTY_BASE + STACK_PENALTY_SCALED;
  return STACK_PENALTY_BASE + (stackSlotsUsed / maxStackSlots) * STACK_PENALTY_SCALED;
}

/**
 * Score a candidate dispatch using depth-1: simulate one lap, return cubes cleared
 * minus stack penalty if the attacker returns to stack.
 */
function scoreDepth1(
  board: BoardSnapshot,
  att: BotAttacker,
  firingSequence: FiringLine[],
  fli?: FiringLineIndex,
): { cubesCleared: number; depleted: boolean } {
  const attClone = cloneAttacker(att);
  const { result, undo } = simulateLapWithUndo(board, attClone, firingSequence, fli);
  undoLap(board, undo, fli);
  return { cubesCleared: result.cubesCleared, depleted: result.attackerDepleted };
}

// ── Unified Scoring ──────────────────────────────────────────────────

interface ScoringContext {
  board: BoardSnapshot;
  firingSequence: FiringLine[];
  fli?: FiringLineIndex;
  reachMap: Map<number, number>;
  maxReachable: number;
  maxAmmoInPool: number;
  maxStackSlots: number;
  stackSlotsUsed: number;
  conveyorSlotsUsed: number;
  queueLanes?: (BotAttacker | null)[][];
  stackAttackers: BotAttacker[];
  conveyorAttackers: BotAttacker[];
}

export interface ScoreBreakdown {
  cubeClearedScore: number;
  depletionBonus: number;
  lowAmmoBonus: number;
  colorDemandBonus: number;
  stackSourceBonus: number;
  stackOverflowPenalty: number;
  queueLookaheadBonus: number;
  wasteRatioPenalty: number;
  conveyorCrowdingPenalty: number;
  slotPressureBonus: number;
  grindBonus: number;
  ammoEfficiencyPenalty: number;
  unblockBonus: number;
  queueExposureBonus: number;
  budgetUrgencyPenalty: number;
  total: number;
  cubesCleared: number;
  depleted: boolean;
}

/**
 * Score a single candidate for unified dispatch. Higher = better.
 *
 * Components:
 *   cubeClearedScore     (0+): raw cubesCleared count from scoreDepth1
 *   depletionBonus       (+200 if depletes, 0 otherwise)
 *   lowAmmoBonus         (0-50): 50 * (1 - totalAmmo/maxAmmoInPool)
 *   colorDemandBonus     (0-30+): 30 * (reachableForThisColor/maxReachableAcrossColors), multi-orb can exceed 30
 *   stackSourceBonus     (+20 for stack, 0 for queue)
 *   stackOverflowPenalty (0 to -500, queue only): -500 * clamp(pressure,0,1)^2
 *   queueLookaheadBonus  (0-40, queue only): value of next 1-2 items behind front in same lane
 */
function scoreCandidate(
  candidate: DispatchCandidate,
  ctx: ScoringContext,
): ScoreBreakdown {
  // Run depth-1 simulation (inlined to access post-sim board state for unblockBonus)
  const attClone = cloneAttacker(candidate.attacker);
  const { result: simResult, undo } = simulateLapWithUndo(ctx.board, attClone, ctx.firingSequence, ctx.fli);

  // Compute post-simulation reachability BEFORE undoing
  const postReachMap = buildReachMap(ctx.board);

  // Count newly reachable cubes (cubes reachable after sim that weren't before)
  let newlyReachable = 0;
  const newlyReachablePerColor = new Map<number, number>();
  for (const [color, postCount] of postReachMap) {
    const preCount = ctx.reachMap.get(color) ?? 0;
    if (postCount > preCount) {
      const delta = postCount - preCount;
      newlyReachable += delta;
      newlyReachablePerColor.set(color, delta);
    }
  }

  // Undo the simulation to restore original board state
  undoLap(ctx.board, undo, ctx.fli);

  const d1 = { cubesCleared: simResult.cubesCleared, depleted: simResult.attackerDepleted };

  // cubeClearedScore: raw count (0-100 typical range)
  const cubeClearedScore = d1.cubesCleared;

  // ── Reachable cubes for this attacker's colors ──
  let reachableForAttacker = 0;
  for (const orb of candidate.attacker.orbs) {
    reachableForAttacker += ctx.reachMap.get(orb.colorIndex) ?? 0;
  }

  const totalAmmo = candidate.attacker.orbs.reduce((s, o) => s + o.ammo, 0);

  // depletionBonus: proportional based on depletion likelihood
  let depletionBonus: number;
  if (d1.depleted) {
    depletionBonus = 200; // Confirmed depletion from simulation
  } else if (totalAmmo <= reachableForAttacker) {
    depletionBonus = 150; // Very likely to deplete (ammo fits within reachable)
  } else {
    // Proportional: how close to depleting? Higher ratio = more likely
    depletionBonus = totalAmmo > 0 ? 150 * (reachableForAttacker / totalAmmo) : 0;
  }

  // lowAmmoBonus: relative to max ammo in pool. Lowest ammo → +50, highest → 0
  let lowAmmoBonus = 0;
  if (ctx.maxAmmoInPool > 0) {
    lowAmmoBonus = Math.max(0, 50 * (1 - totalAmmo / ctx.maxAmmoInPool));
  }

  // colorDemandBonus: how much the board needs this color. Multi-orb: sum across orbs
  let colorDemandBonus = 0;
  if (ctx.maxReachable > 0) {
    let demand = 0;
    for (const orb of candidate.attacker.orbs) {
      demand += ctx.reachMap.get(orb.colorIndex) ?? 0;
    }
    colorDemandBonus = 30 * (demand / ctx.maxReachable);
  }

  // stackSourceBonus: +20 for productive stack candidates, +1 for zero-clearing stack
  // (keeps slight stack preference to avoid overflow, but queue easily wins with any clears)
  const stackSourceBonus = candidate.source === 'stack' ? (d1.cubesCleared > 0 ? 20 : 1) : 0;

  // stackOverflowPenalty: queue-only, quadratic. Depletion-safe candidates exempt.
  // Only count actual stack occupancy — conveyor attackers are actively working, not stuck.
  let stackOverflowPenalty = 0;
  if (candidate.source === 'queue' && !d1.depleted) {
    const pressure = Math.min(1, ctx.stackSlotsUsed / ctx.maxStackSlots);
    stackOverflowPenalty = -500 * pressure * pressure;
  }

  // queueLookaheadBonus: for queue candidates, estimate value of next 1-2 items behind
  // front in the same lane. Dispatching a "useless" front item unblocks valuable followers.
  let queueLookaheadBonus = 0;
  if (candidate.source === 'queue' && ctx.queueLanes) {
    const lane = ctx.queueLanes[candidate.index];
    if (lane && lane.length > 1) {
      // lane[0] is the front (being dispatched), lane[1] is next, lane[2] is second next
      for (let i = 1; i <= Math.min(2, lane.length - 1); i++) {
        const nextAtt = lane[i];
        if (!nextAtt) continue;
        let nextValue = 0;
        for (const orb of nextAtt.orbs) {
          const reachable = ctx.reachMap.get(orb.colorIndex) ?? 0;
          // Estimate cubes this attacker could clear: min(ammo, reachable)
          nextValue += Math.min(orb.ammo, reachable);
        }
        const weight = i === 1 ? 0.3 : 0.1;
        queueLookaheadBonus += weight * nextValue;
      }
      queueLookaheadBonus = Math.min(40, queueLookaheadBonus);
    }
  }

  // wasteRatioPenalty: penalizes attackers carrying much more ammo than they can use
  // Stack candidates are exempt — they MUST be re-dispatched, penalizing waste is counterproductive
  let wasteRatioPenalty = 0;
  if (candidate.source !== 'stack' && reachableForAttacker > 0 && totalAmmo > reachableForAttacker) {
    const wasteRatio = (totalAmmo - reachableForAttacker) / totalAmmo;
    wasteRatioPenalty = -50 * wasteRatio;
  }

  // conveyorCrowdingPenalty: discourages dispatching when conveyor is nearly full
  let conveyorCrowdingPenalty = 0;
  if (ctx.conveyorSlotsUsed >= 4) {
    conveyorCrowdingPenalty = -30;
  } else if (ctx.conveyorSlotsUsed >= 3) {
    conveyorCrowdingPenalty = -10;
  }

  // slotPressureBonus: incentivizes clearing the stack when it has 2+ attackers
  let slotPressureBonus = 0;
  if (candidate.source === 'stack' && ctx.stackSlotsUsed >= 2) {
    slotPressureBonus = 50 * (ctx.stackSlotsUsed / ctx.maxStackSlots);
  }

  // grindBonus: rewards dispatching for colors where cubes are deeply buried (total >> reachable)
  let grindBonus = 0;
  {
    // Count total cubes of this attacker's colors on the board
    let totalCubesOfColor = 0;
    let reachableCubesOfColor = 0;
    for (const orb of candidate.attacker.orbs) {
      // Count total cubes from the grid
      let colorTotal = 0;
      for (let r = 0; r < ctx.board.rows; r++) {
        for (let c = 0; c < ctx.board.cols; c++) {
          for (const ci of ctx.board.grid[r][c]) {
            if (ci === orb.colorIndex) colorTotal++;
          }
        }
      }
      // Also count mega block HP for this color
      for (const mb of ctx.board.megaBlocks.values()) {
        if (mb.colorIndex === orb.colorIndex) colorTotal += mb.hp;
      }
      totalCubesOfColor += colorTotal;
      reachableCubesOfColor += ctx.reachMap.get(orb.colorIndex) ?? 0;
    }
    if (reachableCubesOfColor > 0) {
      const ratio = totalCubesOfColor / reachableCubesOfColor;
      if (ratio > 10) {
        grindBonus = 20 * Math.log2(ratio);
      }
    }
    // Scale grindBonus by how efficiently the attacker can use its ammo.
    // When reachable << ammo, the attacker cycles futilely clearing tiny amounts.
    if (grindBonus > 0) {
      const eff = Math.min(1, reachableCubesOfColor / Math.max(1, totalAmmo));
      grindBonus *= Math.max(0.15, eff);
    }
  }

  // ammoEfficiencyPenalty: penalize when ammo greatly exceeds reachable cubes
  let ammoEfficiencyPenalty = 0;
  if (reachableForAttacker > 0) {
    const ammoToReachableRatio = totalAmmo / reachableForAttacker;
    if (ammoToReachableRatio > 2) {
      ammoEfficiencyPenalty = -5.0 * (ammoToReachableRatio - 2);
    }
  }

  // ── Shared color budget data (used by unblockBonus, queueExposureBonus, budgetUrgencyPenalty) ──
  const totalCellsPerColor = new Map<number, number>();
  for (let r = 0; r < ctx.board.rows; r++) {
    for (let c = 0; c < ctx.board.cols; c++) {
      for (const ci of ctx.board.grid[r][c]) {
        totalCellsPerColor.set(ci, (totalCellsPerColor.get(ci) ?? 0) + 1);
      }
    }
  }
  for (const mb of ctx.board.megaBlocks.values()) {
    totalCellsPerColor.set(mb.colorIndex, (totalCellsPerColor.get(mb.colorIndex) ?? 0) + mb.hp);
  }

  const totalAmmoPerColor = new Map<number, number>();
  if (ctx.queueLanes) {
    for (const lane of ctx.queueLanes) {
      for (const att of lane) {
        if (!att) continue;
        for (const orb of att.orbs) {
          totalAmmoPerColor.set(orb.colorIndex, (totalAmmoPerColor.get(orb.colorIndex) ?? 0) + orb.ammo);
        }
      }
    }
  }
  for (const att of ctx.stackAttackers) {
    for (const orb of att.orbs) {
      totalAmmoPerColor.set(orb.colorIndex, (totalAmmoPerColor.get(orb.colorIndex) ?? 0) + orb.ammo);
    }
  }
  for (const att of ctx.conveyorAttackers) {
    for (const orb of att.orbs) {
      totalAmmoPerColor.set(orb.colorIndex, (totalAmmoPerColor.get(orb.colorIndex) ?? 0) + orb.ammo);
    }
  }

  // unblockBonus: weight newly-reachable cells by inverse color tightness
  let unblockBonusRaw = 0;
  for (const [color, delta] of newlyReachablePerColor) {
    const totalCells = totalCellsPerColor.get(color) ?? 0;
    const colorAmmo = totalAmmoPerColor.get(color) ?? 0;
    const tightness = totalCells > 0 ? colorAmmo / totalCells : 999;
    const weight = Math.min(4.0, 2.0 / Math.max(0.01, tightness));
    unblockBonusRaw += delta * weight;
  }
  const unblockBonus = Math.min(120, unblockBonusRaw);

  // queueExposureBonus: rewards dispatching that exposes cells matching upcoming queue colors
  // Gated by stack pressure — full bonus when stack is low, zero when nearly full (survival mode)
  let queueExposureBonus = 0;
  if (ctx.queueLanes) {
    const queueDemand = new Map<number, number>();
    let scanned = 0;
    for (const lane of ctx.queueLanes) {
      for (const att of lane) {
        if (!att || scanned >= 10) break;
        for (const orb of att.orbs) {
          queueDemand.set(orb.colorIndex, (queueDemand.get(orb.colorIndex) ?? 0) + orb.ammo);
        }
        scanned++;
      }
      if (scanned >= 10) break;
    }

    let exposureRaw = 0;
    for (const [color, delta] of newlyReachablePerColor) {
      const demand = queueDemand.get(color) ?? 0;
      exposureRaw += delta * demand;
    }
    exposureRaw *= 3.0;

    const stackRatio = ctx.stackSlotsUsed / ctx.maxStackSlots;
    if (stackRatio < 0.4) {
      queueExposureBonus = Math.min(120, exposureRaw);
    } else if (stackRatio < 0.8) {
      queueExposureBonus = Math.min(120, exposureRaw) * (1 - stackRatio);
    } else {
      queueExposureBonus = 0; // survival mode
    }
  }

  // budgetUrgencyPenalty: penalizes dispatching tight-budget color attackers when cells are buried
  let budgetUrgencyPenalty = 0;
  for (const orb of candidate.attacker.orbs) {
    const totalCells = totalCellsPerColor.get(orb.colorIndex) ?? 0;
    const colorAmmo = totalAmmoPerColor.get(orb.colorIndex) ?? 0;
    if (totalCells === 0) continue;
    const tightness = colorAmmo / totalCells;
    const reachable = ctx.reachMap.get(orb.colorIndex) ?? 0;
    const reachableRatio = reachable / totalCells;
    if (tightness < 1.5 && reachableRatio < 0.4) {
      budgetUrgencyPenalty += -40 * (1 - reachableRatio);
    }
  }

  const total = cubeClearedScore + depletionBonus + lowAmmoBonus
    + colorDemandBonus + stackSourceBonus + stackOverflowPenalty + queueLookaheadBonus
    + wasteRatioPenalty + conveyorCrowdingPenalty + slotPressureBonus + grindBonus
    + ammoEfficiencyPenalty + unblockBonus + queueExposureBonus + budgetUrgencyPenalty;

  return {
    cubeClearedScore,
    depletionBonus,
    lowAmmoBonus,
    colorDemandBonus,
    stackSourceBonus,
    stackOverflowPenalty,
    queueLookaheadBonus,
    wasteRatioPenalty,
    conveyorCrowdingPenalty,
    slotPressureBonus,
    grindBonus,
    ammoEfficiencyPenalty,
    unblockBonus,
    queueExposureBonus,
    budgetUrgencyPenalty,
    total,
    cubesCleared: d1.cubesCleared,
    depleted: d1.depleted,
  };
}

// ── Recursive Depth-N Lookahead ──────────────────────────────────────

/**
 * Build the next GameSnapshot for recursive searchBest calls.
 * Adjusts stack, queue, board, and conveyor state after a simulated dispatch+lap.
 */
function buildNextSnapshot(
  prev: GameSnapshot,
  dispatched: DispatchCandidate,
  boardAfter: BoardSnapshot,
  attAfterLap: BotAttacker,
  lapResult: LapResult,
): GameSnapshot {
  // Stack: remove dispatched (if from stack), add back attacker if not depleted
  const nextStack: BotAttacker[] = [];
  for (let i = 0; i < prev.stackAttackers.length; i++) {
    if (dispatched.source === 'stack' && i === dispatched.index) continue;
    nextStack.push(prev.stackAttackers[i]);
  }
  if (!lapResult.attackerDepleted) {
    nextStack.push(attAfterLap);
  }

  // Queue: shift the dispatched lane (remove front, reveal next), clone others
  const nextQueueLanes = prev.queueLanes.map((lane, i) => {
    if (dispatched.source === 'queue' && i === dispatched.index) {
      // Remove front, advance queue
      return lane.slice(1).map(a => a ? cloneAttacker(a) : null);
    }
    return lane.map(a => a ? cloneAttacker(a) : null);
  });

  // Conveyor: empty (post-lap, attacker has completed its lap)
  const nextConveyor: BotAttacker[] = [];

  // Slot counts
  const nextStackUsed = nextStack.length;
  const nextConveyorUsed = 0;

  return {
    board: boardAfter,  // shared reference; caller manages undo after recursion
    stackAttackers: nextStack,
    queueLanes: nextQueueLanes,
    conveyorAttackers: nextConveyor,
    maxConveyorSlots: prev.maxConveyorSlots,
    conveyorSlotsUsed: nextConveyorUsed,
    maxStackSlots: prev.maxStackSlots,
    stackSlotsUsed: nextStackUsed,
  };
}

/**
 * Recursive depth-N lookahead dispatch scoring.
 *
 * Evaluates all viable candidates (stack + queue), simulates each one,
 * then optionally recurses deeper. Returns the best candidate with its
 * cumulative score, cubes cleared, and stack delta.
 */
export function searchBest(
  snapshot: GameSnapshot,
  firingSequence: FiringLine[],
  maxDepth: number,
  beamWidth: number = Infinity,
  fli?: FiringLineIndex,
): SearchResult | null {
  const availableSlots = snapshot.maxConveyorSlots - snapshot.conveyorSlotsUsed;

  // Build FiringLineIndex if not provided by caller (top-level entry point).
  // Recursive calls pass the same index (mutated in-place, undone after each candidate).
  const lineIdx = fli ?? buildFiringLineIndex(snapshot.board, firingSequence);

  // Build candidates from stack and queue
  const allCandidates: DispatchCandidate[] = [];
  for (let i = 0; i < snapshot.stackAttackers.length; i++) {
    allCandidates.push({
      source: 'stack',
      index: i,
      attacker: snapshot.stackAttackers[i],
    });
  }
  for (let lane = 0; lane < snapshot.queueLanes.length; lane++) {
    const qf = snapshot.queueLanes[lane][0];  // front of lane
    if (!qf) continue;
    allCandidates.push({
      source: 'queue',
      index: lane,
      attacker: qf,
    });
  }

  // Hard rule: for same-color stack attackers, only consider the lowest-ammo one.
  const filteredCandidates = filterLowestAmmoPerColor(allCandidates);

  // Dedup by orb signature (colorIndex:ammo pairs)
  const seen = new Set<string>();
  const deduped: DispatchCandidate[] = [];
  for (const cand of filteredCandidates) {
    const sig = cand.attacker.orbs
      .map(o => `${o.colorIndex}:${o.ammo}`)
      .join(',');
    if (seen.has(sig)) continue;
    seen.add(sig);
    deduped.push(cand);
  }

  // Filter by conveyor capacity
  let viable = deduped.filter(c => c.attacker.size <= availableSlots);
  if (viable.length === 0) return null;

  // ── Change 1 (searchBest): Build duplicate-color sets for soft penalty ──
  // Queue candidates matching active conveyor/stack colors get a scoring penalty
  // (soft preference), NOT a hard skip. Never prevents evaluation.
  const sbActiveStackColors = new Set<number>();
  for (const sa of snapshot.stackAttackers) {
    for (const orb of sa.orbs) {
      if (orb.ammo > 0) sbActiveStackColors.add(orb.colorIndex);
    }
  }
  const sbActiveConveyorColors = new Set<number>();
  for (const ca of snapshot.conveyorAttackers) {
    for (const orb of ca.orbs) {
      if (orb.ammo > 0) sbActiveConveyorColors.add(orb.colorIndex);
    }
  }
  const sbConveyorEmpty = snapshot.conveyorAttackers.length === 0;

  // ── Change 2 (searchBest): Compute max stack ammo for low-ammo bonus ──
  let sbMaxStackAmmo = 0;
  for (const c of viable) {
    if (c.source === 'stack') {
      const totalAmmo = c.attacker.orbs.reduce((s, o) => s + o.ammo, 0);
      if (totalAmmo > sbMaxStackAmmo) sbMaxStackAmmo = totalAmmo;
    }
  }

  // If beam pruning is active, pre-score all candidates at depth-1
  // and only recurse deeper for the top beamWidth candidates
  let deepIndices: Set<number> | null = null;
  if (beamWidth < viable.length && maxDepth > 1) {
    const d1Scores = viable.map((cand, i) => {
      const a = cloneAttacker(cand.attacker);
      const { result: lap, undo } = simulateLapWithUndo(snapshot.board, a, firingSequence, lineIdx);
      undoLap(snapshot.board, undo, lineIdx);
      return { index: i, score: lap.cubesCleared };
    });
    d1Scores.sort((a, b) => b.score - a.score);
    deepIndices = new Set(d1Scores.slice(0, beamWidth).map(s => s.index));
  }

  let best: SearchResult | null = null;

  for (let ci = 0; ci < viable.length; ci++) {
    const cand = viable[ci];

    // ── Change 1 (searchBest): Compute duplicate-color penalty for queue candidates ──
    let sbDupePenalty = 0;
    if (cand.source === 'queue') {
      const candColors = cand.attacker.orbs.filter(o => o.ammo > 0).map(o => o.colorIndex);
      const allDuplicated = candColors.every(ci2 =>
        sbActiveConveyorColors.has(ci2) || sbActiveStackColors.has(ci2)
      );
      if (allDuplicated) {
        sbDupePenalty = sbConveyorEmpty ? 0.1 : 0.5;
      }
    }

    // Note: Queue stack-occupancy penalty and conveyor conservation penalty are
    // applied only at the top-level evaluateDispatch, NOT in recursive searchBest.
    // The lookahead evaluates theoretical follow-ups; penalizing queue candidates
    // recursively would prevent the bot from "seeing through" useless front
    // attackers to useful ones behind them.

    // Mutate board in-place, simulate lap, undo after evaluation
    const attClone = cloneAttacker(cand.attacker);
    const { result: lapResult, undo } = simulateLapWithUndo(snapshot.board, attClone, firingSequence, lineIdx);

    try {
      const delta = computeStackDelta(cand.source, lapResult.attackerDepleted);
      const penaltyWt = stackPenaltyWeight(snapshot.stackSlotsUsed, snapshot.maxStackSlots);
      const d1Bonus = lapResult.cubesCleared * 0.1;
      let score = lapResult.cubesCleared + d1Bonus - delta * penaltyWt - sbDupePenalty;

      // ── Change 2 (searchBest): Low-ammo bonus for stack candidates ──
      if (cand.source === 'stack' && sbMaxStackAmmo > 1) {
        const totalAmmo = cand.attacker.orbs.reduce((s, o) => s + o.ammo, 0);
        const lowAmmoRatio = 1 - (totalAmmo - 1) / (sbMaxStackAmmo - 1);
        const LOW_AMMO_BONUS_MAX = 5.0;
        score += lowAmmoRatio * LOW_AMMO_BONUS_MAX;
      }

      let totalCubes = lapResult.cubesCleared;
      let depthCubes = [lapResult.cubesCleared];
      let totalDelta = delta;

      // Recurse deeper if maxDepth > 1 and this candidate is in the beam
      let deeperPositive = false;
      if (maxDepth > 1 && (!deepIndices || deepIndices.has(ci))) {
        const nextSnap = buildNextSnapshot(snapshot, cand, snapshot.board, attClone, lapResult);
        // Pass the same FiringLineIndex — it's been updated by the lap and will be undone after
        const deeper = searchBest(nextSnap, firingSequence, maxDepth - 1, beamWidth, lineIdx);
        if (deeper && deeper.score > 0) {
          deeperPositive = true;
          // Geometric discount: 0.9 normally, 0.25 if current depth cleared 0 cubes
          const discount = lapResult.cubesCleared === 0 ? 0.25 : 0.9;
          score += deeper.score * discount;
          totalCubes += deeper.cubesCleared;
          depthCubes = depthCubes.concat(deeper.depthCubes);
          totalDelta += deeper.stackDelta;
        }
      }

      // Hopeless check: clears nothing, no positive future, doesn't free a stack slot
      if (lapResult.cubesCleared === 0 && !deeperPositive && delta >= 0) {
        score = -Infinity;
      }

      if (best === null || score > best.score) {
        best = {
          candidateIndex: ci,
          score,
          cubesCleared: totalCubes,
          depthCubes,
          stackDelta: totalDelta,
        };
      }
    } finally {
      undoLap(snapshot.board, undo, lineIdx);
    }
  }

  return best;
}

/**
 * Unified scoring dispatch — every candidate gets a numeric score, best wins.
 * No hard blocks except conveyor capacity. Replaces the old 5-phase priority system.
 *
 * Score = cubeClearedScore + depletionBonus + lowAmmoBonus + colorDemandBonus
 *       + stackSourceBonus + stackOverflowPenalty
 *
 * CRITICAL: Never returns null/wait when conveyor has space AND candidates exist.
 */
export function evaluateDispatch(
  board: BoardSnapshot,
  candidates: DispatchCandidate[],
  firingSequence: FiringLine[],
  stackAttackers: BotAttacker[],
  queueFronts: (BotAttacker | null)[],  // front attacker per lane (null if empty/frozen)
  maxConveyorSlots: number,
  conveyorSlotsUsed: number,
  maxStackSlots: number,
  stackSlotsUsed: number,
  conveyorAttackers: BotAttacker[] = [],
  trace?: BotTrace,
  maxDepth: number = 2,
  queueLanes?: (BotAttacker | null)[][],  // optional deep queue visibility for lookahead
  stallState?: StallState,  // kept for signature compat — ignored internally
): DispatchDecision | null {
  // Hard rule: for same-color stack attackers, only consider the lowest-ammo one.
  candidates = filterLowestAmmoPerColor(candidates);

  if (candidates.length === 0) return { action: 'wait' };

  // Build FiringLineIndex once for the initial board state.
  const lineIdx = buildFiringLineIndex(board, firingSequence);

  // Pre-simulate what current conveyor attackers will clear before evaluating candidates.
  // This prevents double-counting.
  const conveyorUndoActions: UndoAction[] = [];

  if (conveyorAttackers.length > 0) {
    for (const convAtt of conveyorAttackers) {
      const attClone = {
        orbs: convAtt.orbs.map(o => ({ colorIndex: o.colorIndex, ammo: o.ammo })),
        size: convAtt.size,
      };
      const { undo } = simulateLapWithUndo(board, attClone, firingSequence, lineIdx);
      conveyorUndoActions.push(...undo);
    }
  }
  // Board is now in post-conveyor state. Conveyor undo happens in finally block.
  try {
    // ── Step 1: Conveyor full → wait ──
    if (conveyorSlotsUsed >= maxConveyorSlots) {
      return { action: 'wait' };
    }

    // ── Step 2: Capacity-filter candidates ──
    const viable = candidates.filter(
      cand => cand.attacker.size + conveyorSlotsUsed <= maxConveyorSlots
    );

    if (viable.length === 0) return { action: 'wait' };

    // ── Step 3: Check if board has any cubes remaining ──
    let boardHasCubes = false;
    outer:
    for (let r = 0; r < board.rows; r++) {
      for (let c = 0; c < board.cols; c++) {
        if (board.grid[r][c].length > 0) { boardHasCubes = true; break outer; }
      }
    }
    if (!boardHasCubes && board.megaBlocks.size === 0) {
      return { action: 'wait' };
    }

    // ── Step 4: Build scoring context ──
    const reachMap = buildReachMap(board);
    let maxReachable = 0;
    for (const count of reachMap.values()) {
      if (count > maxReachable) maxReachable = count;
    }

    let maxAmmoInPool = 0;
    for (const cand of viable) {
      const ta = cand.attacker.orbs.reduce((s, o) => s + o.ammo, 0);
      if (ta > maxAmmoInPool) maxAmmoInPool = ta;
    }

    const ctx: ScoringContext = {
      board, firingSequence, fli: lineIdx,
      reachMap, maxReachable, maxAmmoInPool,
      maxStackSlots: maxStackSlots > 0 ? maxStackSlots : 1,  // prevent div-by-zero
      stackSlotsUsed, conveyorSlotsUsed,
      queueLanes,
      stackAttackers: stackAttackers,
      conveyorAttackers: conveyorAttackers,
    };

    // ── Step 5: Score all candidates ──
    let bestCandidate: DispatchCandidate | null = null;
    let bestScore = -Infinity;
    let bestBreakdown: ScoreBreakdown | null = null;

    // ── Trace setup ──
    let traceBoardState: TraceEntry['boardState'] | undefined;
    let traceConveyorAtts: TraceEntry['conveyorAttackers'] | undefined;
    const traceBreakdowns: { cand: DispatchCandidate; bd: ScoreBreakdown }[] = [];

    if (trace) {
      const totalPerColor = new Map<number, number>();
      for (let r = 0; r < board.rows; r++) {
        for (let c = 0; c < board.cols; c++) {
          for (const ci of board.grid[r][c]) {
            totalPerColor.set(ci, (totalPerColor.get(ci) ?? 0) + 1);
          }
        }
      }
      for (const mb of board.megaBlocks.values()) {
        totalPerColor.set(mb.colorIndex, (totalPerColor.get(mb.colorIndex) ?? 0) + mb.hp);
      }
      traceBoardState = [];
      for (const [ci, total] of totalPerColor) {
        traceBoardState.push({
          color: colorName(ci),
          total,
          reachable: reachMap.get(ci) ?? 0,
        });
      }
      traceBoardState.sort((a, b) => b.total - a.total);

      traceConveyorAtts = conveyorAttackers.map(a => ({
        color: colorName(a.orbs[0]?.colorIndex ?? -1),
        ammo: a.orbs.reduce((s, o) => s + o.ammo, 0),
      }));
    }

    for (const cand of viable) {
      const bd = scoreCandidate(cand, ctx);

      if (trace) {
        traceBreakdowns.push({ cand, bd });
      }

      if (bd.total > bestScore) {
        bestScore = bd.total;
        bestCandidate = cand;
        bestBreakdown = bd;
      }
    }

    // ── Step 6: Emit trace ──
    if (trace && bestCandidate && bestBreakdown) {
      const traceCandidates: TraceEntry['candidates'] = traceBreakdowns.map(({ cand, bd }) => ({
        source: cand.source,
        color: colorName(cand.attacker.orbs[0]?.colorIndex ?? -1),
        ammo: cand.attacker.orbs.reduce((s, o) => s + o.ammo, 0),
        orbs: cand.attacker.orbs.length,
        depthCubes: [bd.cubesCleared],
        stackDelta: cand.source === 'stack' ? (bd.depleted ? -1 : 0) : (bd.depleted ? 0 : 1),
        totalScore: bd.total,
      }));

      const chosenColor = colorName(bestCandidate.attacker.orbs[0]?.colorIndex ?? -1);
      const chosenAmmo = bestCandidate.attacker.orbs.reduce((s, o) => s + o.ammo, 0);
      const bb = bestBreakdown;
      trace.push({
        tick: 0,
        boardState: traceBoardState!,
        conveyorAttackers: traceConveyorAtts!,
        candidates: traceCandidates,
        waitScore: null,
        decision: 'dispatch',
        chosenCandidate: {
          source: bestCandidate.source,
          color: chosenColor,
          ammo: chosenAmmo,
          score: bb.total,
        },
        reason: `unified-score: ${chosenColor} from ${bestCandidate.source} | cubes=${bb.cubeClearedScore} depletion=${bb.depletionBonus.toFixed(1)} lowAmmo=${bb.lowAmmoBonus.toFixed(1)} demand=${bb.colorDemandBonus.toFixed(1)} stackSrc=${bb.stackSourceBonus} overflow=${bb.stackOverflowPenalty.toFixed(1)} lookahead=${bb.queueLookaheadBonus.toFixed(1)} waste=${bb.wasteRatioPenalty.toFixed(1)} conv=${bb.conveyorCrowdingPenalty} slot=${bb.slotPressureBonus.toFixed(1)} grind=${bb.grindBonus.toFixed(1)} eff=${bb.ammoEfficiencyPenalty.toFixed(1)} unblock=${bb.unblockBonus.toFixed(1)} qExpose=${bb.queueExposureBonus.toFixed(1)} budgetUrg=${bb.budgetUrgencyPenalty.toFixed(1)} → total=${bb.total.toFixed(1)}`,
      });
    }

    // ── Step 7: Return best ──
    if (bestCandidate && bestBreakdown) {
      return { action: 'dispatch', candidate: bestCandidate, score: bestScore, breakdown: bestBreakdown, candidateCount: viable.length };
    }

    // No viable candidates after filtering
    return { action: 'wait' };
  } finally {
    // Undo conveyor pre-simulation mutations to restore board to original state
    undoLap(board, conveyorUndoActions, lineIdx);
  }
}

/**
 * Compute the predicted change in stack count after a dispatch completes.
 */
function computeStackDelta(source: 'stack' | 'queue', depleted: boolean): number {
  if (source === 'stack') return depleted ? -1 : 0;
  return depleted ? 0 : 1;
}

// ── Trace Formatter ─────────────────────────────────────────────────

/**
 * Format trace entries as human/AI readable text.
 * Used by both CLI (audit --trace) and runtime (AutoplaySystem download).
 */
export function formatTraceText(entries: TraceEntry[]): string {
  const lines: string[] = [];
  let dispatchCount = 0;
  let waitCount = 0;
  let stackDispatches = 0;
  let queueDispatches = 0;

  for (const entry of entries) {
    lines.push(`=== Tick ${entry.tick} ===`);

    // Board state
    const boardParts = entry.boardState.map(
      bs => `${bs.color}=${bs.total}(${bs.reachable} reachable)`
    );
    lines.push(`Board: ${boardParts.join(', ') || '[empty]'}`);

    // Conveyor
    if (entry.conveyorAttackers.length === 0) {
      lines.push('Conveyor: [empty]');
    } else {
      const convParts = entry.conveyorAttackers.map(a => `${a.color}(${a.ammo})`);
      lines.push(`Conveyor: ${convParts.join(', ')}`);
    }

    // Candidates
    if (entry.candidates.length === 0) {
      lines.push('Candidates: [none]');
    } else {
      lines.push('Candidates:');
      for (const c of entry.candidates) {
        const src = `[${c.source.toUpperCase()}]`;
        const col = c.color.padEnd(8);
        const deltaStr = c.stackDelta >= 0 ? `+${c.stackDelta}` : `${c.stackDelta}`;
        const hopelessTag = c.hopeless ? ' [HOPELESS]' : '';
        const scoreStr = c.totalScore === -Infinity ? '-Inf' : c.totalScore.toFixed(1);
        const depthStr = c.depthCubes.map((n, i) => `d${i + 1}:${n}`).join(' ');
        lines.push(
          `  ${src} ${col} ammo=${c.ammo} orbs=${c.orbs} | ${depthStr} delta=${deltaStr} | score=${scoreStr}${hopelessTag}`
        );
      }
    }

    // Wait score
    if (entry.waitScore !== null) {
      lines.push(`Wait: score=${entry.waitScore.toFixed(1)}`);
    } else {
      lines.push('Wait: not evaluated (conveyor empty)');
    }

    // Decision
    if (entry.decision === 'wait') {
      lines.push(`-> WAIT (${entry.reason})`);
      waitCount++;
    } else if (entry.chosenCandidate) {
      const ch = entry.chosenCandidate;
      lines.push(
        `-> DISPATCH [${ch.source.toUpperCase()}] ${ch.color} ammo=${ch.ammo} (score ${ch.score.toFixed(1)} — ${entry.reason})`
      );
      dispatchCount++;
      if (ch.source === 'stack') stackDispatches++;
      else queueDispatches++;
    }

    lines.push('');
  }

  // Summary
  lines.push('--- Summary ---');
  lines.push(`Total ticks: ${entries.length}`);
  lines.push(`Total dispatches: ${dispatchCount} (${stackDispatches} from stack, ${queueDispatches} from queue)`);
  lines.push(`Total waits: ${waitCount}`);

  return lines.join('\n');
}
